import { isPaywall } from '@/api/errors'
import { stockLinkOptions } from '@/components/ui/symbol-link'
import { KATHMANDU_TZ } from '@/lib/bsDate'
import type { SignalSide, SignalStock } from '@/schemas/signals'

/**
 * Signal Zone's shared words and small rules: what the components and
 * screens agree on, in a module with no components so each component file
 * exports only components (Fast Refresh) and a loader may import it.
 */

/** True for the 403 `SUBSCRIPTION_REQUIRED` a signals read answers a member whose plan lacks it (D4). */
export const isSignalsLocked = (error: unknown): boolean => isPaywall(error)

/** True when a signal row's symbol is an index or sector sub-index (`useIndexSymbols`). */
export const isIndexRow = (indexSymbols: ReadonlySet<string>, stock: SignalStock): boolean =>
  indexSymbols.has(stock.symbol.toUpperCase())

/**
 * A signal table's `rowLinkOptions`: a stock row opens its company page, and
 * an index or sector row stays a plain row (there is no stock page for it).
 */
export const signalRowLink =
  (indexSymbols: ReadonlySet<string>) => (row: { stock: SignalStock }) =>
    isIndexRow(indexSymbols, row.stock) ? undefined : stockLinkOptions(row.stock.symbol)

/** A board's name. Sentence case: a card title, not navigation. */
export const SIDE_TITLE: Record<SignalSide, string> = {
  green: 'Green signals',
  red: 'Red signals',
}

/** The same two names as the tab strip draws them, in Title Case. */
export const SIDE_TAB_LABEL: Record<SignalSide, string> = {
  green: 'Green Signals',
  red: 'Red Signals',
}

/**
 * "57 stocks", the frames' word (owner 2026-10-12, overturning audit M3's
 * "names": the index and sector rows that ride along are few, and the page
 * names them in the Company column).
 */
export const stocksCount = (count: number): string =>
  `${String(count)} ${count === 1 ? 'stock' : 'stocks'}`

const RUN_CLOCK = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: KATHMANDU_TZ,
})

/**
 * "11:06" — a live run's time on the 24-hour Kathmandu clock, as the frames
 * (f02, f15) print it for the "Signal at" column and the run picker. 24-hour
 * because NEPSE's afternoon runs would otherwise read "1:15" with no way to
 * tell them from the morning's; `formatTime`'s "11:06 AM" is too wide for
 * a picker row. A timestamp that does not parse is a dash, never a guess.
 */
export const formatRunTime = (iso: string): string => {
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? '—' : RUN_CLOCK.format(at)
}
